import { Users, Trophy, Camera, ArrowRight, Bell } from "lucide-react";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { getCoverPhoto } from "@/lib/eventPhotos";
import { getEventStatus, getWinner, getParticipantCount } from "@/lib/events";
import { KindBadge } from "./KindBadge";
import { LocationDisplay } from "./LocationDisplay";
import { WinnerDisplay } from "./WinnerDisplay";
import logoImage from "@/assets/logo-image.png";
import type { Quizrun } from "@/types/domain";
import type { EventResultsData } from "@/types/results";
import type { UseQueryResult } from "@tanstack/react-query";

interface EventCardProps {
  quizrun: Quizrun;
  resultsQuery?: UseQueryResult<EventResultsData | null>;
  // Position in the visible list, seeds the placeholder gradient ramp.
  colorSeed?: number;
}

const formatDateRange = (startDate: string, endDate: string) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const formatOptions: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  };

  if (start.toDateString() === end.toDateString()) {
    return start.toLocaleDateString("en-GB", formatOptions);
  }

  if (
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear()
  ) {
    return `${start.getDate()} – ${end.toLocaleDateString("en-GB", formatOptions)}`;
  }

  return `${start.toLocaleDateString("en-GB", formatOptions)} – ${end.toLocaleDateString("en-GB", formatOptions)}`;
};

export const EventCard = ({ quizrun: qr, resultsQuery, colorSeed }: EventCardProps) => {
  const eventStatus = getEventStatus(qr);
  const isLoading = resultsQuery?.isLoading ?? false;
  const results = resultsQuery?.data?.results ?? null;
  const participantCount = results ? getParticipantCount(results) : null;
  const winner = results ? getWinner(results) : null;
  const cover = getCoverPhoto(qr.id);
  const isCompleted = eventStatus === "completed";
  // Any attached event means people can take part (on-site even without a Luma
  // link), so registration is effectively open for a non-finished quizrun.
  const hasEvents = (qr.events?.length ?? 0) > 0;

  const renderPlaceholder = () => {
    if (isCompleted) return null;
    const seed = colorSeed ?? qr.number ?? qr.id;
    // Ramp red-violet -> blue -> green by list position; specials sit in-sequence.
    const hue1 = (330 - (seed - 1) * 30 + 360) % 360;
    const hue2 = (hue1 - 40 + 360) % 360;
    return (
      <div
        className="relative w-20 h-20 md:w-[180px] md:h-[120px] shrink-0 rounded-lg overflow-hidden flex items-center justify-center text-white"
        style={{
          background: `linear-gradient(135deg, hsl(${hue1} 70% 55%), hsl(${hue2} 65% 45%))`,
        }}
      >
        <img
          src={logoImage}
          alt=""
          aria-hidden="true"
          className="h-10 w-10 md:h-14 md:w-14 opacity-90"
          style={{ filter: "brightness(0) invert(1)" }}
        />
        {qr.number != null && (
          <span className="absolute bottom-1.5 right-2 text-[10px] md:text-xs font-semibold tracking-wider opacity-80">
            #{qr.number}
          </span>
        )}
      </div>
    );
  };

  const renderCoverPhoto = () => {
    if (!cover) return renderPlaceholder();

    const imgEl = (
      <div className="relative w-20 h-20 md:w-[180px] md:h-[120px] shrink-0 rounded-lg overflow-hidden bg-muted group/photo">
        <img
          src={cover.src}
          alt={cover.alt}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-300 group-hover/photo:scale-110"
        />
        {qr.photo_link && (
          <div className="absolute inset-0 bg-black/0 group-hover/photo:bg-black/30 transition-colors flex items-center justify-center">
            <Camera className="h-5 w-5 text-white opacity-0 group-hover/photo:opacity-100 transition-opacity drop-shadow-lg" />
          </div>
        )}
      </div>
    );

    if (qr.photo_link) {
      return (
        <a
          href={qr.photo_link}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="View photo album"
          className="block"
        >
          {imgEl}
        </a>
      );
    }

    return imgEl;
  };

  return (
    <article className="group relative rounded-xl border bg-card transition-all duration-200 hover:shadow-md hover:border-primary/40">
      {/* Full-card click target. Sits underneath interactive children
          via z-0; children opt into clicks with relative z-10. */}
      <Link
        to={`/events/${qr.id}`}
        className="absolute inset-0 z-0 rounded-xl"
        aria-label={`View ${qr.title}`}
      />
      <div className="relative z-[1] flex gap-3 p-3 md:gap-4 md:p-4 pointer-events-none">
        {/* Cover photo or placeholder. Only opt-in to pointer events when
            there's actually a photo link to click — otherwise let clicks
            fall through to the underlying card Link. */}
        <div className={qr.photo_link ? "pointer-events-auto" : ""}>
          {renderCoverPhoto()}
        </div>

        {/* Event details */}
        <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5 md:gap-1.5">
          {/* Title + kind badge + date + status badges */}
          <div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="font-bold text-foreground group-hover:text-primary transition-colors text-sm md:text-base leading-tight line-clamp-2 md:line-clamp-1">
                {qr.title}
              </h3>
              <KindBadge kind={qr.kind} />
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="text-xs text-muted-foreground">
                {formatDateRange(qr.date_start, qr.date_finish)}
              </span>
            </div>
          </div>

          {/* Location — compact on mobile */}
          <div className="hidden md:block">
            <LocationDisplay quizrun={qr} showPrice={false} />
          </div>
          <div className="md:hidden">
            <LocationDisplay quizrun={qr} compact showPrice={false} />
          </div>

          {/* Register CTA for upcoming events that have something to attend. */}
          {!isCompleted && hasEvents && (
            <div className="pointer-events-none">
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary group/cta">
                Register
                <ArrowRight className="h-3 w-3" />
              </span>
            </div>
          )}

          {/* Subscribe CTA only when there's nothing to attend yet — no events
              attached at all. Once an event exists, registration is open. */}
          {eventStatus === "registration-open" && !hasEvents && (
            <div className="pointer-events-auto relative z-10">
              <Link
                to="/#notify-form"
                className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors group/cta"
              >
                <Bell className="h-3 w-3" />
                Subscribe not to miss
                <ArrowRight className="h-3 w-3 group-hover/cta:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          )}

          {/* Meta row: participants, photos, winner — only for completed
              events. For upcoming events these are noise (count is –, photos
              don't exist yet, no winner). */}
          {isCompleted && (
            <div className="hidden md:flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <Users className="h-3.5 w-3.5 shrink-0" />
                {isLoading ? (
                  <Skeleton className="h-4 w-6" />
                ) : participantCount ? (
                  <span className="font-medium text-foreground">
                    {participantCount}
                  </span>
                ) : (
                  <span>–</span>
                )}
              </span>

              {!cover && qr.photo_link && (
                <a
                  href={qr.photo_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pointer-events-auto relative z-10 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors"
                >
                  📸 Photos
                </a>
              )}

              <span className="pointer-events-auto relative z-10">
                <WinnerDisplay winner={winner} isLoading={isLoading} />
              </span>
            </div>
          )}

          {/* Mobile compact meta — same rule: only for completed. */}
          {isCompleted && (
            <div className="md:hidden flex items-center gap-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-0.5">
                <Users className="h-3 w-3" />
                {isLoading ? "…" : participantCount || "–"}
              </span>
              {winner?.onsite && (
                <span className="inline-flex items-center gap-0.5">
                  <Trophy className="h-3 w-3 text-amber-500" />
                  {winner.onsite.id ? (
                    <Link
                      to={`/participant/${winner.onsite.id}`}
                      className="pointer-events-auto relative z-10 text-primary hover:underline font-medium"
                    >
                      {winner.onsite.name}
                    </Link>
                  ) : (
                    <span className="font-medium">{winner.onsite.name}</span>
                  )}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};
